import { validateVariantSwitch } from '../helpers'
import clone from 'clone'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { CloseOutlined, CopyOutlined, WifiOutlined } from '@ant-design/icons'
import React, { useCallback, useMemo } from 'react'
import { getInitialVariant } from '../notification-builder'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { ActionDispatcher } from '../types'
import { AccountDto } from '../../../dtos/account.dto'
import { DomainDto } from '../../../dtos/domain'
import { CampaignDto } from '../../../features/campaigns/dtos/campaign-dto'
import { PushlyAi, useVariantManager, VariantManager, VariantActionItem } from '@pushly/aqe/lib/components'
import { useFieldSuggestionResultsManager } from '../hooks/use-field-suggestion-results-setter'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'

type NotificationVariantManagerProps = {
    state: INotificationBuilderState
    dispatch: ActionDispatcher
    onInitialVariantInit?: (variant: NotificationVariantModel) => void
    domain: DomainDto
    account?: AccountDto
    campaign?: CampaignDto
    readonly?: boolean
}

export const useNotificationVariantManager = (variants?: NotificationVariantModel[]) => {
    return useVariantManager<NotificationVariantModel>(variants)
}

export const NotificationVariantManager = (props: NotificationVariantManagerProps) => {
    const { state, dispatch, onInitialVariantInit, domain, account, campaign, readonly = false } = props
    const { loading, variants, selectedVariantIdx } = state

    const appState = useService(AppState)

    const userPreferences = useUserPreferences(appState.currentUser?.id, props.domain.id)

    const activeVariant = variants[selectedVariantIdx]
    const activeContentModel = activeVariant.getContent()

    const { applyFieldSuggestionResults } = useFieldSuggestionResultsManager(activeContentModel, state.channels, noop)

    const handleAddVariant = useCallback(
        async (sourceVariantIdx?: number, options: { generateContent?: boolean } = {}) => {
            const hasSourceVariant = sourceVariantIdx !== undefined

            if (await validateVariantSwitch(state, domain, dispatch)) {
                /**
                 * Holds the data for the next variant to be used, derived by cloning an existing variant.
                 * The source for cloning is determined by `sourceVariantIdx` if it is defined;
                 * otherwise, it defaults to `state.selectedVariantIdx`.
                 *
                 * When `hasSourceVariant` is false, we replace the content with a blank slate
                 * derived from the initial variant builder. We keep the rest of the cloned variant
                 * so the currently selected audience and delivery details are preserved.
                 */
                const nextVariant = clone(variants[sourceVariantIdx ?? state.selectedVariantIdx])
                if (!hasSourceVariant) {
                    const emptyVariant = getInitialVariant(
                        domain,
                        account,
                        state,
                        userPreferences,
                        onInitialVariantInit,
                        campaign,
                    )
                    nextVariant.setContent(emptyVariant.getContent())
                }

                if (options.generateContent && hasSourceVariant) {
                    const generatedContent = await state.pushlyAi.generateNotificationVariants(nextVariant)
                    if (!generatedContent) {
                        simpleNotification(
                            'info',
                            'We were unable to generate any suggestions for the provided Landing URL.',
                        )
                    } else {
                        const update = applyFieldSuggestionResults(generatedContent)
                        if (update) {
                            nextVariant.setContent(update)
                        }
                    }
                }

                dispatch({
                    entity: 'variants',
                    type: 'add',
                    data: { variant: nextVariant },
                })
            }
        },
        [
            userPreferences,
            state,
            domain,
            account,
            onInitialVariantInit,
            campaign,
            dispatch,
            selectedVariantIdx,
            state.pushlyAi,
            applyFieldSuggestionResults,
        ],
    )

    const variantActions: VariantActionItem[] = useMemo(() => {
        const actions: VariantActionItem[] = []
        if (variants.length < 4) {
            actions.push(
                ...([
                    // disabled until backend supports it
                    {
                        key: 'duplicate_with_ai',
                        label: (
                            <span>
                                Generate <b>Pushly AI</b> Variant
                            </span>
                        ),
                        icon: <PushlyAi />,
                        onClick: (idx) => handleAddVariant(idx, { generateContent: true }),
                    },
                    {
                        key: 'duplicate',
                        label: 'Clone',
                        icon: <CopyOutlined />,
                        onClick: (idx) => handleAddVariant(idx),
                    },
                ] satisfies VariantActionItem[]),
            )
        }

        if (variants.length > 1) {
            if (variants.length < 4) {
                actions.push({ type: 'divider' })
            }

            actions.push(
                ...([
                    {
                        key: 'remove',
                        label: 'Remove',
                        icon: <CloseOutlined />,
                        onClick: (indexToRemove) =>
                            dispatch({
                                entity: 'variants',
                                type: 'remove',
                                data: { indexToRemove },
                            }),
                    },
                    { type: 'divider' },
                    {
                        key: 'remove_others',
                        label: 'Remove All Other Variants',
                        icon: <CloseOutlined />,
                        onClick: (indexToKeep) =>
                            dispatch({
                                entity: 'variants',
                                type: 'remove-others',
                                data: { indexToKeep },
                            }),
                    },
                ] satisfies VariantActionItem[]),
            )
        }

        return actions
    }, [variants.length, handleAddVariant, dispatch])

    const primaryActions = useMemo(
        () => [
            {
                key: 'duplicate_active',
                label: 'Clone Current',
                icon: <CopyOutlined />,
                onClick: () => handleAddVariant(selectedVariantIdx),
            },
            {
                key: 'add_black',
                label: 'Add Blank Variant',
                icon: <WifiOutlined />,
                onClick: () => handleAddVariant(),
            },
        ],
        [handleAddVariant, selectedVariantIdx],
    )

    const variantManagerVariantActionsHidden = readonly || state.editNotificationType === 'single'
    const variantManagerPrimaryActionsHidden = readonly
    const variantManagerPrimaryActionsDisabled = state.editNotificationType === 'single'
    const variantManagerPrimaryActionsDisabledReason =
        state.editNotificationType === 'single' ? 'Existing Notifications cannot be converted to tests.' : undefined

    return (
        <VariantManager<NotificationVariantModel>
            className="notification-variant-manager"
            loading={loading}
            variants={state.variants}
            selectedIndex={selectedVariantIdx}
            primaryActions={variantManagerPrimaryActionsHidden ? false : primaryActions}
            primaryActionsDisabled={variantManagerPrimaryActionsDisabled}
            primaryActionsDisabledReason={variantManagerPrimaryActionsDisabledReason}
            variantActions={variantManagerVariantActionsHidden ? false : variantActions}
            onSelectVariant={async (idx) => {
                if (await validateVariantSwitch(state, domain, dispatch)) {
                    dispatch({
                        type: 'patch',
                        entity: 'root',
                        data: { selectedVariantIdx: idx },
                    })
                }
            }}
        />
    )
}
